import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
    Plug, RefreshCw, Save, Wifi, ShieldCheck, Database, Calendar,
    Send, ListChecks, CheckCircle2, XCircle, Trash2, ChevronDown, ChevronRight,
    Webhook, Copy, KeyRound, Layers, Eye, EyeOff,
} from "lucide-react";

const IntegrazionePrenova = () => {
    const [settings, setSettings] = useState({
        api_url: "", api_key: "", restaurant_id: "",
        push_path: "", pull_path: "", webhook_secret: "",
        auto_sync: false, last_sync: null, last_test_restaurant_name: null,
    });
    const [webhookInfo, setWebhookInfo] = useState({ url: "", events: [] });
    const [testing, setTesting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [pushing, setPushing] = useState(false);
    const [layoutSyncing, setLayoutSyncing] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [pushResult, setPushResult] = useState(null);
    const [pullResult, setPullResult] = useState(null);
    const [layoutResult, setLayoutResult] = useState(null);
    const [logs, setLogs] = useState([]);
    const [openLog, setOpenLog] = useState(null);
    const [showKey, setShowKey] = useState(false);
    const [showSecret, setShowSecret] = useState(false);

    const load = async () => {
        try {
            const [s, wh] = await Promise.all([
                api.get("/prenova/settings"),
                api.get("/prenova/webhook-info"),
            ]);
            setSettings({
                api_url: "", api_key: "", restaurant_id: "",
                push_path: "", pull_path: "", webhook_secret: "",
                auto_sync: false, last_sync: null,
                ...s.data,
            });
            setWebhookInfo(wh.data);
        } catch (_e) { /* */ }
    };

    const loadLogs = useCallback(async () => {
        try {
            const { data } = await api.get("/prenova/sync-logs?limit=50");
            setLogs(data);
        } catch (_e) { /* */ }
    }, []);

    useEffect(() => {
        load();
        loadLogs();
        const id = setInterval(loadLogs, 6000);
        return () => clearInterval(id);
    }, [loadLogs]);

    const save = async () => {
        try {
            await api.post("/prenova/settings", settings);
            toast.success("Configurazione salvata");
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const test = async () => {
        setTesting(true); setTestResult(null);
        try {
            const { data } = await api.post("/prenova/test-connection");
            setTestResult(data);
            data.ok ? toast.success(data.message) : toast.error(data.message);
        } catch (e) { toast.error(formatError(e)); }
        setTesting(false);
        loadLogs();
    };

    const importRes = async () => {
        setImporting(true); setPullResult(null);
        try {
            const { data } = await api.post("/prenova/import-reservations");
            setPullResult(data);
            data.ok ? toast.success(data.message) : toast.warning(data.message);
            load();
        } catch (e) { toast.error(formatError(e)); }
        setImporting(false);
        loadLogs();
    };

    const pushState = async () => {
        setPushing(true); setPushResult(null);
        try {
            const { data } = await api.post("/prenova/push-state");
            setPushResult(data);
            if (data.ok) toast.success(data.message);
            else toast.warning(data.message);
        } catch (e) { toast.error(formatError(e)); }
        setPushing(false);
        loadLogs();
    };

    const syncLayout = async () => {
        setLayoutSyncing(true); setLayoutResult(null);
        try {
            const { data } = await api.post("/prenova/sync-layout");
            setLayoutResult(data);
            data.ok ? toast.success(data.message) : toast.warning(data.message);
        } catch (e) { toast.error(formatError(e)); }
        setLayoutSyncing(false);
        loadLogs();
    };

    const clearLogs = async () => {
        if (!window.confirm("Cancellare tutti i log?")) return;
        try {
            await api.delete("/prenova/sync-logs");
            setLogs([]);
            toast.success("Log puliti");
        } catch (e) { toast.error(formatError(e)); }
    };

    const copy = (txt, label = "Link") => {
        navigator.clipboard.writeText(txt);
        toast.success(`${label} copiato`);
    };

    return (
        <div className="space-y-6 max-w-5xl" data-testid="prenova-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Integrazione</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                        <Plug className="w-9 h-9 text-brand" /> Collega <span className="text-brand">renova360</span>
                    </h1>
                    <p className="text-elegant mt-3 max-w-2xl">
                        Integrazione bidirezionale real-time con il portale renova360: pull prenotazioni, push stato tavoli, conferme,
                        webhook entranti firmati HMAC. Tutti gli endpoint usano i path ufficiali renova360.
                    </p>
                </div>
            </div>

            {/* Quick info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                    <Database className="w-5 h-5 text-brand mb-2" />
                    <div className="text-sm font-bold text-warm">Tavoli + stato live</div>
                    <div className="text-xs text-elegant mt-1">Snapshot bulk e per-tavolo verso /tables/{`{id}`}/status</div>
                </div>
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                    <Calendar className="w-5 h-5 text-brand mb-2" />
                    <div className="text-sm font-bold text-warm">Prenotazioni</div>
                    <div className="text-xs text-elegant mt-1">Pull ogni 60s · Push confirm/cancel/seat/no-show</div>
                </div>
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                    <Webhook className="w-5 h-5 text-brand mb-2" />
                    <div className="text-sm font-bold text-warm">Webhook real-time</div>
                    <div className="text-xs text-elegant mt-1">HMAC SHA256 verificato, eventi reservation.* e ping</div>
                </div>
            </div>

            {/* CREDENZIALI */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                    <KeyRound className="w-5 h-5 text-brand" />
                    <div className="font-display text-xl font-bold">Credenziali API renova360</div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">API URL (base)</label>
                    <input className="w-full h-12 mt-1 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono text-sm"
                        placeholder="https://renova360-reservation.it/api/external/v1"
                        value={settings.api_url} onChange={(e) => setSettings({ ...settings, api_url: e.target.value })} data-testid="prenova-url" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">API Key (43 caratteri)</label>
                    <div className="relative">
                        <input type={showKey ? "text" : "password"} className="w-full h-12 mt-1 px-3 pr-12 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono text-sm"
                            placeholder="rnv_••••••••••••••••••••••••••••••••••••••"
                            value={settings.api_key} onChange={(e) => setSettings({ ...settings, api_key: e.target.value })} data-testid="prenova-key" />
                        <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 w-9 h-9 flex items-center justify-center text-elegant hover:text-brand">
                            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">Restaurant ID</label>
                    <input className="w-full h-12 mt-1 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono text-sm"
                        placeholder="rst_xxxxxxxxxxxx"
                        value={settings.restaurant_id} onChange={(e) => setSettings({ ...settings, restaurant_id: e.target.value })} data-testid="prenova-rest-id" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">Push Path</label>
                    <input className="w-full h-12 mt-1 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono text-sm"
                        placeholder="https://renova360-reservation.it/api/external/v1/snapshot"
                        value={settings.push_path || ""} onChange={(e) => setSettings({ ...settings, push_path: e.target.value })} data-testid="prenova-push-path" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">Pull Path</label>
                    <input className="w-full h-12 mt-1 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono text-sm"
                        placeholder="https://renova360-reservation.it/api/external/v1/reservations"
                        value={settings.pull_path || ""} onChange={(e) => setSettings({ ...settings, pull_path: e.target.value })} data-testid="prenova-pull-path" />
                </div>

                <label className="flex items-center gap-3 mt-2">
                    <input type="checkbox" checked={settings.auto_sync} onChange={(e) => setSettings({ ...settings, auto_sync: e.target.checked })} className="w-5 h-5 accent-[#14E5BC]" data-testid="prenova-autosync" />
                    <div>
                        <div className="text-sm">Pull automatico ogni 60 secondi</div>
                        <div className="text-xs text-elegant">Job in background che importa nuove prenotazioni dal portale.</div>
                    </div>
                </label>

                {settings.last_sync && (
                    <div className="text-xs text-elegant border-t border-[#1f2c44] pt-3 flex items-center gap-2">
                        <RefreshCw className="w-3 h-3" />
                        Ultimo pull: <span className="text-warm">{new Date(settings.last_sync).toLocaleString("it-IT")}</span>
                        {settings.last_test_restaurant_name && (
                            <span className="ml-3">· Ristorante: <span className="text-brand font-bold">{settings.last_test_restaurant_name}</span></span>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2">
                    <button onClick={save} data-testid="prenova-save-button" className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2 hover:bg-brand-light transition-colors">
                        <Save className="w-4 h-4" /> Salva
                    </button>
                    <button onClick={test} disabled={testing} data-testid="prenova-test-button" className="h-12 px-5 border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand-soft transition-colors disabled:opacity-50">
                        <Wifi className="w-4 h-4" /> {testing ? "Verifica..." : "Test Connessione"}
                    </button>
                    <button onClick={importRes} disabled={importing} data-testid="prenova-import-button" className="h-12 px-5 border border-[#1f2c44] rounded-md flex items-center gap-2 hover:border-brand transition-colors disabled:opacity-50">
                        <RefreshCw className={`w-4 h-4 ${importing ? "animate-spin" : ""}`} /> {importing ? "Pull..." : "Pull prenotazioni"}
                    </button>
                    <button onClick={syncLayout} disabled={layoutSyncing} data-testid="prenova-sync-layout" className="h-12 px-5 border border-[#1f2c44] rounded-md flex items-center gap-2 hover:border-brand transition-colors disabled:opacity-50">
                        <Layers className={`w-4 h-4 ${layoutSyncing ? "animate-spin" : ""}`} /> {layoutSyncing ? "..." : "Sincronizza Layout"}
                    </button>
                    <button onClick={pushState} disabled={pushing} data-testid="prenova-push-state" className="h-12 px-5 bg-brand-soft border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand/20 transition-colors disabled:opacity-50">
                        <Send className={`w-4 h-4 ${pushing ? "animate-pulse" : ""}`} /> {pushing ? "Invio snapshot..." : "Invia snapshot"}
                    </button>
                </div>

                {testResult && (
                    <div className={`text-sm rounded-md p-3 ${testResult.ok ? "bg-brand/10 border border-brand/40 text-brand" : "bg-red-900/20 border border-red-700/40 text-red-300"}`} data-testid="test-result">
                        {testResult.ok ? "✓ " : "✗ "}{testResult.message}
                        {testResult.restaurant_name && (
                            <div className="text-xs mt-1 text-warm">Nome ristorante riconosciuto: <strong>{testResult.restaurant_name}</strong></div>
                        )}
                    </div>
                )}

                {pushResult && (
                    <div className={`text-sm rounded-md p-3 space-y-2 ${pushResult.ok ? "bg-brand/10 border border-brand/40" : "bg-amber-900/20 border border-amber-700/40"}`} data-testid="push-result">
                        <div className={pushResult.ok ? "text-brand font-bold" : "text-amber-300 font-bold"}>
                            {pushResult.ok ? "✓ " : "⚠ "}{pushResult.message}
                        </div>
                        {pushResult.summary && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-warm">
                                <Stat label="Tavoli inviati" value={pushResult.summary.pushed_tables} />
                                <Stat label="Liberi" value={pushResult.summary.free_tables} accent="text-brand" />
                                <Stat label="Occupati" value={pushResult.summary.occupied_tables} accent="text-amber-400" />
                                <Stat label="Prenotaz. inviate" value={pushResult.summary.pushed_reservations} />
                            </div>
                        )}
                    </div>
                )}

                {pullResult && (
                    <div className={`text-sm rounded-md p-3 space-y-2 ${pullResult.ok ? (pullResult.raw_count ? "bg-brand/10 border border-brand/40" : "bg-amber-900/20 border border-amber-700/40") : "bg-red-900/20 border border-red-700/40"}`} data-testid="pull-result">
                        <div className={pullResult.ok && pullResult.raw_count ? "text-brand font-bold" : "text-amber-300 font-bold"}>
                            {pullResult.ok && pullResult.raw_count ? "✓ " : "⚠ "}{pullResult.message}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-warm">
                            <Stat label="Ricevute" value={pullResult.raw_count} />
                            <Stat label="Nuove" value={pullResult.imported} accent="text-brand" />
                            <Stat label="Aggiornate" value={pullResult.updated} accent="text-amber-400" />
                            <Stat label="Range" value={pullResult.date_from && pullResult.date_to ? `${pullResult.date_from?.slice(5)}→${pullResult.date_to?.slice(5)}` : "—"} />
                        </div>
                        {pullResult.url && (
                            <div className="text-[10px] font-mono text-elegant break-all">URL: {pullResult.url}</div>
                        )}
                        {pullResult.body_preview && (
                            <details className="text-[11px]">
                                <summary className="cursor-pointer text-elegant uppercase tracking-wider">Body Preview ricevuto</summary>
                                <pre className="bg-deep border border-[#1f2c44] rounded p-2 mt-1 overflow-auto max-h-40 whitespace-pre-wrap break-all">{pullResult.body_preview}</pre>
                            </details>
                        )}
                    </div>
                )}

                {layoutResult && (
                    <div className={`text-sm rounded-md p-3 space-y-2 ${layoutResult.raw_count ? "bg-brand/10 border border-brand/40" : "bg-amber-900/20 border border-amber-700/40"}`} data-testid="layout-result">
                        <div className={layoutResult.raw_count ? "text-brand font-bold" : "text-amber-300 font-bold"}>
                            {layoutResult.raw_count ? "✓ " : "⚠ "}{layoutResult.message}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-warm">
                            <Stat label="Ricevuti" value={layoutResult.raw_count} />
                            <Stat label="Nuovi" value={layoutResult.imported} accent="text-brand" />
                            <Stat label="Aggiornati" value={layoutResult.updated} accent="text-amber-400" />
                        </div>
                        {layoutResult.body_preview && (
                            <details className="text-[11px]">
                                <summary className="cursor-pointer text-elegant uppercase tracking-wider">Body Preview ricevuto</summary>
                                <pre className="bg-deep border border-[#1f2c44] rounded p-2 mt-1 overflow-auto max-h-40 whitespace-pre-wrap break-all">{layoutResult.body_preview}</pre>
                            </details>
                        )}
                    </div>
                )}
            </div>

            {/* WEBHOOK INBOUND */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 space-y-4" data-testid="prenova-webhook-section">
                <div className="flex items-center gap-2">
                    <Webhook className="w-5 h-5 text-brand" />
                    <div className="font-display text-xl font-bold">Webhook in entrata da Prenova</div>
                </div>
                <p className="text-elegant text-sm">
                    Configura questo URL nel pannello renova360 per ricevere eventi real-time.
                    Tutte le chiamate vengono verificate con HMAC SHA256.
                </p>

                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">URL pubblico SmartTable</label>
                    <div className="flex items-center gap-2 mt-1">
                        <input readOnly value={webhookInfo.url || ""} className="flex-1 h-12 px-3 bg-deep border border-[#1f2c44] rounded-md font-mono text-sm text-warm"
                            data-testid="webhook-url" />
                        <button onClick={() => copy(webhookInfo.url, "URL webhook")} className="w-12 h-12 rounded border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand flex items-center justify-center">
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-wider text-elegant">Webhook Secret (incolla quello generato da Prenova)</label>
                    <div className="relative">
                        <input type={showSecret ? "text" : "password"} className="w-full h-12 mt-1 px-3 pr-12 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono text-sm"
                            placeholder="whsec_••••••••••••"
                            value={settings.webhook_secret || ""} onChange={(e) => setSettings({ ...settings, webhook_secret: e.target.value })}
                            data-testid="prenova-webhook-secret" />
                        <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 w-9 h-9 flex items-center justify-center text-elegant hover:text-brand">
                            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="text-[11px] text-elegant mt-1">Salva, poi richiedi a Prenova di inviare un evento <code className="text-brand">ping</code> per verificare.</div>
                </div>

                <div className="bg-deep border border-[#1f2c44] rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant mb-2">Eventi gestiti</div>
                    <div className="flex flex-wrap gap-1.5">
                        {(webhookInfo.events || []).map((ev) => (
                            <span key={ev} className="text-[11px] font-mono px-2 py-1 rounded bg-brand-soft border border-brand/30 text-brand">{ev}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* LOG SYNC */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6" data-testid="prenova-logs-section">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <ListChecks className="w-5 h-5 text-brand" />
                        <div className="font-display text-xl font-bold">Log Sincronizzazione</div>
                        <div className="text-xs text-elegant">({logs.length} recenti)</div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={loadLogs} className="h-9 px-3 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" /> Aggiorna
                        </button>
                        <button onClick={clearLogs} className="h-9 px-3 border border-red-700/50 text-red-400 hover:bg-red-900/20 rounded-md text-xs flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Pulisci
                        </button>
                    </div>
                </div>

                {logs.length === 0 ? (
                    <div className="text-elegant text-sm py-8 text-center">
                        Nessun log ancora. Esegui un'operazione (test, pull, push, webhook) per vederla qui.
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[480px] overflow-y-auto">
                        {logs.map((l) => {
                            const ok = l.ok || l?.result?.ok;
                            const ts = new Date(l.timestamp).toLocaleString("it-IT");
                            const isOpen = openLog === l.id;
                            const status = l.status || l?.result?.status || "—";
                            const isWebhook = l.method === "WEBHOOK";
                            return (
                                <div key={l.id} className="bg-deep border border-[#1f2c44] rounded-md text-sm">
                                    <button onClick={() => setOpenLog(isOpen ? null : l.id)}
                                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface" data-testid={`log-${l.id}`}>
                                        {isOpen ? <ChevronDown className="w-4 h-4 text-elegant" /> : <ChevronRight className="w-4 h-4 text-elegant" />}
                                        {ok ? <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                                        <div className={`font-mono text-xs w-20 ${isWebhook ? "text-purple-400" : "text-brand"}`}>
                                            {isWebhook ? `← ${l.event || "WH"}` : l.method}
                                        </div>
                                        <div className="font-mono text-xs text-warm flex-1 truncate">{l.path || l.url || "—"}</div>
                                        <div className="text-xs text-elegant">HTTP {String(status)}</div>
                                        <div className="text-[11px] text-elegant w-40 text-right hidden sm:block">{ts}</div>
                                    </button>
                                    {isOpen && (
                                        <div className="px-4 pb-3 border-t border-[#1f2c44] grid grid-cols-1 lg:grid-cols-2 gap-3 pt-3">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">{isWebhook ? "Body ricevuto" : "Payload inviato"}</div>
                                                <pre className="text-[11px] bg-surface border border-[#1f2c44] rounded p-2 overflow-auto max-h-64 text-warm whitespace-pre-wrap break-all">
{l.payload ? (typeof l.payload === "string" ? l.payload : JSON.stringify(l.payload, null, 2)) : "(nessun payload)"}
                                                </pre>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">{isWebhook ? "Risultato elaborazione" : "Risposta renova360"}</div>
                                                <pre className="text-[11px] bg-surface border border-[#1f2c44] rounded p-2 overflow-auto max-h-64 text-warm whitespace-pre-wrap break-all">
{JSON.stringify(l.result || { status: l.status }, null, 2)}
                                                </pre>
                                                <div className="text-[10px] text-elegant mt-2 flex justify-between">
                                                    <span>{ts}</span>
                                                    {l.url && <span className="font-mono">{l.url}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="bg-brand-soft border border-brand/30 rounded-lg p-4 text-sm">
                <div className="font-bold text-brand mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Test di accettazione</div>
                <ul className="text-elegant space-y-1 list-disc pl-5 text-[13px]">
                    <li><strong className="text-warm">Test connessione</strong> → handshake OK con nome ristorante</li>
                    <li><strong className="text-warm">Pull prenotazioni</strong> → legge prenotazioni del giorno</li>
                    <li><strong className="text-warm">Conferma prenotazione</strong> da SmartTable → in Prenova lo stato passa a "confirmed" entro 5s</li>
                    <li><strong className="text-warm">Webhook ping</strong> firmato → ricevuto con firma HMAC verificata</li>
                    <li><strong className="text-warm">Nuova prenotazione</strong> da sito Prenova → compare in SmartTable entro 2s tramite webhook reservation.created</li>
                    <li><strong className="text-warm">Snapshot tavoli</strong> occupati riflesso in Prenova (verifica /tables)</li>
                </ul>
            </div>
        </div>
    );
};

const Stat = ({ label, value, accent = "text-warm" }) => (
    <div className="bg-deep border border-[#1f2c44] rounded px-2 py-2">
        <div className="text-elegant text-[10px] uppercase">{label}</div>
        <div className={`font-bold text-lg ${accent}`}>{value ?? "—"}</div>
    </div>
);

export default IntegrazionePrenova;
